export type FeedSourceActor = "user" | "agent" | "system" | "customer_method";

export interface FeedSource {
  actor: FeedSourceActor;
  clientId?: string;
  /** Stable authored handle for a transient producer, when available. */
  operationId?: string;
  jobId?: string;
  turnId?: string;
  method?: string;
}

export type FeedIconToken =
  | "neutral"
  | "info"
  | "database"
  | "search"
  | "clock"
  | "sparkles"
  | "check"
  | "warning"
  | "error";

export type FeedFeedbackTone =
  | "info"
  | "working"
  | "awaiting"
  | "success"
  | "warning"
  | "error";

/** Tones that describe live state; terminal success belongs in durable history. */
export type FeedTransientFeedbackTone = Exclude<FeedFeedbackTone, "success">;

export type FeedItemKind =
  | "message"
  | "feedback"
  | "objects"
  | "table"
  | "artifact"
  | "file"
  | "action_suggestion"
  | "prompt";

export interface FeedTarget {
  className: string;
  id: string;
  label?: string;
}

export interface FeedItemBase {
  id: string;
  sequence: number;
  occurredAt: number;
  kind: FeedItemKind;
  operationId: string;
  batchIndex: number;
  source?: FeedSource;
}

export interface MessageFeedItem extends FeedItemBase {
  kind: "message";
  payload: {
    role: "user" | "assistant" | "system";
    text: string;
    format?: "plain_text" | "markdown";
    target?: FeedTarget;
    inReplyToPromptId?: string;
    answer?: string | number | boolean | null;
    answerDisplay?: string;
  };
}

export interface FeedbackFeedItem extends FeedItemBase {
  kind: "feedback";
  payload: {
    text: string;
    icon?: FeedIconToken;
    tone: FeedFeedbackTone;
    audience: "customer";
    action?: {
      actionId: string;
      kind: "frontend" | "backend" | "system";
      label: string;
      status: "queued" | "done" | "failed";
    };
    code?: string;
  };
}

export type FeedObjectReference =
  | { type: "entry"; path: string }
  | { type: "list"; name: string }
  | { type: "variable"; name: string };

export interface ObjectsFeedItem extends FeedItemBase {
  kind: "objects";
  payload: {
    label?: string;
    refs: FeedObjectReference[];
    fallback?: Array<{
      className?: string;
      id: string;
      label?: string;
    }>;
  };
}

export type FeedTableCell =
  | string
  | number
  | boolean
  | null
  | {
      kind: "relative_time" | "date";
      value: string | number;
    };

export interface FeedTableColumn {
  id: string;
  label: string;
}

export interface FeedTableRowReference {
  entryPath?: string;
  className?: string;
  id?: string;
  label?: string;
}

export interface FeedTableRow {
  id: string;
  reference?: FeedTableRowReference;
  cells: FeedTableCell[];
}

export interface FeedTableProjection {
  id: string;
  label?: string;
  source?: string;
  columns: FeedTableColumn[];
  rows: FeedTableRow[];
}

export interface TableFeedItem extends FeedItemBase {
  kind: "table";
  payload: {
    tableId: string;
    label?: string;
    columns: FeedTableColumn[];
    rows: FeedTableRow[];
    truncated?: boolean;
  };
}

export interface ArtifactFeedItem extends FeedItemBase {
  kind: "artifact";
  payload: {
    artifactId: string;
    fallback: {
      kind: "effect" | "batch" | "state_path";
      label: string;
      status?: string;
      description?: string;
      target?: FeedTarget;
    };
  };
}

export type FeedFileSource =
  | "dock"
  | "sdk"
  | "agent"
  | "runtime"
  | "processor"
  | "library"
  | "imported";

export interface FileFeedItem extends FeedItemBase {
  kind: "file";
  payload: {
    fileId: string;
    fallback: {
      filename: string;
      contentType?: string;
      byteLength?: number;
      status?: string;
      source?: FeedFileSource;
    };
  };
}

/**
 * The bounded subset needed to create an artifact from a suggestion.
 *
 * This is deliberately data-only. It must never contain executable UI or
 * callback values.
 */
export interface NormalizedSuggestedArtifact {
  version?: 1;
  kind: "effect" | "batch" | "state_path";
  label: string;
  description?: string;
  status?: string;
  target?: FeedTarget;
  inputSchema?: Record<string, unknown>;
  inputValues?: Record<string, unknown>;
  relationships?: Record<string, string | string[] | null>;
  policy?: Record<string, unknown>;
  statePlan?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface ActionSuggestionFeedItem extends FeedItemBase {
  kind: "action_suggestion";
  payload: {
    suggestionId: string;
    label: string;
    description?: string;
    target?: FeedTarget;
    proposedArtifact?: NormalizedSuggestedArtifact;
  };
}

export interface PromptFeedItem extends FeedItemBase {
  kind: "prompt";
  payload: {
    promptId: string;
    type: "choice" | "confirm" | "input";
    title: string;
    message: string;
    options?: Array<{ value: string; label: string }>;
    placeholder?: string;
    defaultValue?: string | boolean | null;
    allowEmpty?: boolean;
    openedAt: number;
  };
}

export type FeedItem =
  | MessageFeedItem
  | FeedbackFeedItem
  | ObjectsFeedItem
  | TableFeedItem
  | ArtifactFeedItem
  | FileFeedItem
  | ActionSuggestionFeedItem
  | PromptFeedItem;

export interface TransientFeedItemBase {
  id: string;
  ordinal: number;
  revision: number;
  createdAt: number;
  updatedAt: number;
  expiresAt?: number;
  source?: Omit<FeedSource, "actor" | "clientId"> & {
    actor: Exclude<FeedSourceActor, "user">;
    promptId?: string;
  };
}

export interface TransientMessageFeedItem extends TransientFeedItemBase {
  kind: "message";
  producerRevision: number;
  payload: {
    role: "assistant";
    text: string;
    format?: "plain_text" | "markdown";
  };
}

export interface TransientFeedbackFeedItem extends TransientFeedItemBase {
  kind: "feedback";
  payload: {
    text: string;
    icon?: FeedIconToken;
    tone: FeedTransientFeedbackTone;
    audience: "customer";
  };
}

export type TransientFeedItem =
  | TransientMessageFeedItem
  | TransientFeedbackFeedItem;

export interface FeedSnapshot {
  tail: FeedItem[];
  transients: TransientFeedItem[];
  revision: number;
  documentEpoch: number;
  documentRevision: number;
  lastSequence: number;
  archivedThroughSequence: number;
  hasOlder: boolean;
  isHydrated: boolean;
  isRepairing: boolean;
  error: Error | null;
}

export interface FeedListOptions {
  afterSequence?: number;
  beforeSequence?: number;
  limit?: number;
}

export interface FeedPage {
  items: FeedItem[];
  firstSequence: number | null;
  lastSequence: number | null;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
}

export interface FeedSubscribeOptions {
  afterSequence?: number;
}

export type FeedSubscriptionChange =
  | { type: "append"; items: FeedItem[] }
  | { type: "transients"; items: TransientFeedItem[]; revision: number }
  | {
      type: "resource_refresh";
      documentRevision: number;
      resourceIds?: string[];
    }
  | { type: "reset"; snapshot: FeedSnapshot };

export interface SessionFeedApi {
  getSnapshot(): FeedSnapshot;
  list(options?: FeedListOptions): Promise<FeedPage>;
  subscribe(
    listener: (change: FeedSubscriptionChange) => void,
    options?: FeedSubscribeOptions,
  ): () => void;
}

function requirePositiveFeedPosition(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 1) {
    throw new Error("Invalid feed chronology.");
  }
  return value;
}

/**
 * Merge independently contiguous archive/live collections by authoritative
 * sequence. Expected overlap collapses by occurrence identity; conflicting
 * IDs or positions fail closed.
 */
export function mergeFeedItemsBySequence(
  ...collections: ReadonlyArray<readonly FeedItem[]>
): FeedItem[] {
  const bySequence = new Map<number, FeedItem>();
  const sequenceById = new Map<string, number>();
  for (const collection of collections) {
    let priorSequence = 0;
    for (const item of collection) {
      const sequence = requirePositiveFeedPosition(item.sequence);
      if (
        sequence <= priorSequence ||
        (priorSequence > 0 && sequence !== priorSequence + 1)
      ) {
        throw new Error("Invalid feed chronology.");
      }
      priorSequence = sequence;
      const id = String(item.id || "");
      const knownSequence = sequenceById.get(id);
      const existing = bySequence.get(sequence);
      if (
        !id ||
        (knownSequence !== undefined && knownSequence !== sequence) ||
        (existing && existing.id !== id)
      ) {
        throw new Error("Invalid feed chronology.");
      }
      if (existing && !sameDurableItem(existing, item)) {
        throw new Error(
          "Invalid feed chronology: immutable occurrence collision.",
        );
      }
      sequenceById.set(id, sequence);
      if (!existing) bySequence.set(sequence, item);
    }
  }
  return [...bySequence.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, item]) => item);
}

/**
 * Select the newest revision of each transient and preserve server ordinal
 * order. Duplicate ordinal ownership is a chronology violation.
 */
export function orderTransientFeedItems(
  items: readonly TransientFeedItem[],
): TransientFeedItem[] {
  const byId = new Map<string, TransientFeedItem>();
  const ordinalById = new Map<string, number>();
  const idByOrdinal = new Map<number, string>();
  for (const item of items) {
    const id = String(item.id || "");
    const ordinal = requirePositiveFeedPosition(item.ordinal);
    const knownOrdinal = ordinalById.get(id);
    const ordinalOwner = idByOrdinal.get(ordinal);
    if (
      !id ||
      (knownOrdinal !== undefined && knownOrdinal !== ordinal) ||
      (ordinalOwner && ordinalOwner !== id)
    ) {
      throw new Error("Invalid feed chronology.");
    }
    ordinalById.set(id, ordinal);
    idByOrdinal.set(ordinal, id);
    const existing = byId.get(id);
    if (
      existing &&
      item.revision === existing.revision &&
      stableValueFingerprint(item) !== stableValueFingerprint(existing)
    ) {
      throw new Error(
        "Invalid feed chronology: conflicting transient revision.",
      );
    }
    if (!existing || item.revision > existing.revision) byId.set(id, item);
  }
  return [...byId.values()].sort((left, right) => left.ordinal - right.ordinal);
}

export type FeedListTransport = (
  options: FeedListOptions,
) => Promise<FeedPage | Record<string, unknown>>;

export type FeedSnapshotRegressionReason =
  | "canonical_deactivation"
  | "invalid_canonical_document"
  | "older_document_epoch"
  | "document_revision_regression"
  | "last_sequence_regression"
  | "last_sequence_without_revision"
  | "feed_revision_regression"
  | "identity_conflict";

export type FeedDiagnostic =
  | {
      type: "gap_detected";
      expectedSequence: number;
      targetSequence: number;
      repairAvailable: boolean;
    }
  | {
      type: "gap_repair";
      outcome: "success" | "failure" | "cancelled";
      durationMs: number;
      pageCount: number;
      repairedThroughSequence: number;
      targetSequence: number;
    }
  | {
      type: "snapshot_regression_rejected";
      reason: FeedSnapshotRegressionReason;
      currentDocumentEpoch: number;
      incomingDocumentEpoch: number;
      currentDocumentRevision: number;
      incomingDocumentRevision: number;
      currentFeedRevision: number;
      incomingFeedRevision: number;
      currentLastSequence: number;
      incomingLastSequence: number;
    }
  | {
      type: "unknown_kind";
      kind: string;
      sequence: number;
      consumer?: "sdk" | "dock";
    };

export type FeedDiagnosticListener = (diagnostic: FeedDiagnostic) => void;

export const GRANULAR_FEED_DIAGNOSTIC_EVENT =
  "granular:feed-diagnostic" as const;

type FeedDiagnosticEventTarget = {
  dispatchEvent?: (event: unknown) => unknown;
  CustomEvent?: new (
    type: string,
    init: { detail: Readonly<FeedDiagnostic> },
  ) => unknown;
};

export function normalizeFeedDiagnosticKind(kind: string): string {
  return /^[a-z][a-z0-9_]{0,63}$/.test(kind) ? kind : "invalid_unknown_kind";
}

export function normalizeFeedDiagnostic(
  diagnostic: FeedDiagnostic,
): FeedDiagnostic {
  if (diagnostic.type !== "unknown_kind") return diagnostic;
  const kind = normalizeFeedDiagnosticKind(diagnostic.kind);
  return kind === diagnostic.kind ? diagnostic : { ...diagnostic, kind };
}

/**
 * Dependency-free production sink for browser SDK and Dock diagnostics.
 *
 * Hosts can listen for `granular:feed-diagnostic` and forward the bounded
 * metadata to their telemetry provider. Server/customer payloads, operation
 * IDs, and resource IDs are not part of the diagnostic union.
 */
export function emitFeedDiagnosticToDefaultSink(
  diagnostic: FeedDiagnostic,
  target: FeedDiagnosticEventTarget = globalThis as FeedDiagnosticEventTarget,
): void {
  try {
    const normalized = normalizeFeedDiagnostic(diagnostic);
    const EventConstructor =
      target.CustomEvent ||
      (globalThis as FeedDiagnosticEventTarget).CustomEvent;
    if (
      typeof target.dispatchEvent !== "function" ||
      typeof EventConstructor !== "function"
    ) {
      return;
    }
    target.dispatchEvent(
      new EventConstructor(GRANULAR_FEED_DIAGNOSTIC_EVENT, {
        detail: Object.freeze({ ...normalized }),
      }),
    );
  } catch {
    // Diagnostics are advisory and must never interfere with feed delivery.
  }
}

export function emitFeedDiagnostic(
  diagnostic: FeedDiagnostic,
  listener?: FeedDiagnosticListener | null,
): void {
  const normalized = normalizeFeedDiagnostic(diagnostic);
  emitFeedDiagnosticToDefaultSink(normalized);
  if (!listener) return;
  try {
    listener(normalized);
  } catch {
    // Keep this warning payload-free even when a user callback throws.
    console.warn("[Granular] Session feed diagnostic listener failed");
  }
}

export interface SessionFeedControllerOptions {
  listTransport?: FeedListTransport | null;
  isHydrated?: boolean;
  /**
   * Receives chronology metadata only. Durable payloads, operation IDs, and
   * resource IDs are deliberately not included.
   */
  onDiagnostic?: FeedDiagnosticListener | null;
  /** Test-only clock seam for deterministic repair duration assertions. */
  now?: () => number;
  /** Test seam for deterministic quiet-feed repair retries. */
  scheduleRepairRetry?: (callback: () => void, delayMs: number) => unknown;
  /** Test seam paired with `scheduleRepairRetry`. */
  cancelRepairRetry?: (handle: unknown) => void;
  /** Initial archive repair retry delay. Defaults to 500ms. */
  initialRepairRetryDelayMs?: number;
  /** Maximum archive repair retry delay. Defaults to 10s. */
  maxRepairRetryDelayMs?: number;
}

export interface FeedDocumentState {
  schemaVersion: 1;
  activation: {
    mode: "canonical";
    activatedAt: number;
  };
  revision: number;
  lastSequence: number;
  archivedThroughSequence: number;
  tail: FeedItem[];
  transientById: Record<string, TransientFeedItem>;
  lastTransientOrdinal: number;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as UnknownRecord;
}

function isSafeInteger(value: unknown, minimum = 0): value is number {
  return (
    typeof value === "number" && Number.isSafeInteger(value) && value >= minimum
  );
}

function finiteInteger(value: unknown, fallback: number, minimum = 0): number {
  return isSafeInteger(value, minimum) ? value : fallback;
}

function cloneAndFreeze<T>(value: T, seen = new WeakMap<object, unknown>()): T {
  if (!value || typeof value !== "object") {
    return value;
  }
  const object = value as object;
  const cached = seen.get(object);
  if (cached) {
    return cached as T;
  }
  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(object, result);
    for (const entry of value) {
      result.push(cloneAndFreeze(entry, seen));
    }
    return Object.freeze(result) as T;
  }
  const result = Object.create(null) as UnknownRecord;
  seen.set(object, result);
  for (const [key, entry] of Object.entries(value as UnknownRecord)) {
    result[key] = cloneAndFreeze(entry, seen);
  }
  return Object.freeze(result) as T;
}

function snapshotWith(
  snapshot: FeedSnapshot,
  patch: Partial<FeedSnapshot>,
): FeedSnapshot {
  return Object.freeze({ ...snapshot, ...patch });
}

export function emptyFeedSnapshot(isHydrated = false): FeedSnapshot {
  return Object.freeze({
    tail: Object.freeze([]) as unknown as FeedItem[],
    transients: Object.freeze([]) as unknown as TransientFeedItem[],
    revision: 0,
    documentEpoch: 0,
    documentRevision: 0,
    lastSequence: 0,
    archivedThroughSequence: 0,
    hasOlder: false,
    isHydrated,
    isRepairing: false,
    error: null,
  });
}

/**
 * Canonical mode is selected solely by the irreversible activation marker.
 *
 * This is intentionally separate from document-shape validation. An activated
 * but temporarily partial/corrupt document must fail closed in canonical mode;
 * treating it as non-canonical would mix two mutually exclusive projections.
 */
export function hasCanonicalSessionFeedActivation(document: unknown): boolean {
  const documentRecord = asRecord(document);
  const feed = asRecord(documentRecord?.feed);
  const activation = asRecord(feed?.activation);
  return activation?.mode === "canonical";
}

/**
 * Capability predicate retained for SDK consumers. It answers whether the
 * session is canonical, not whether the current canonical payload is valid.
 */
export function isCanonicalSessionFeedDocument(document: unknown): boolean {
  return hasCanonicalSessionFeedActivation(document);
}

function canonicalSessionFeedStructureError(document: unknown): Error | null {
  if (!hasCanonicalSessionFeedActivation(document)) return null;
  const documentRecord = asRecord(document);
  const feed = asRecord(documentRecord?.feed);
  const activation = asRecord(feed?.activation);
  if (feed?.schemaVersion !== 1) {
    return new Error("Canonical feed schemaVersion must be 1.");
  }
  if (!isSafeInteger(activation?.activatedAt)) {
    return new Error("Canonical feed activation timestamp is invalid.");
  }
  if (!Array.isArray(feed.tail)) {
    return new Error("Canonical feed tail is not an array.");
  }
  if (!asRecord(feed.transientById)) {
    return new Error("Canonical feed transientById is not an object.");
  }
  if (
    [
      feed.revision,
      feed.lastSequence,
      feed.archivedThroughSequence,
      feed.lastTransientOrdinal,
      documentRecord?.documentEpoch,
      documentRecord?.documentRevision,
    ].some((value) => !isSafeInteger(value))
  ) {
    return new Error("Canonical feed chronology scalar is invalid.");
  }
  if (!isSafeInteger(documentRecord?.documentEpoch, 1)) {
    return new Error("Canonical feed documentEpoch must be positive.");
  }
  return null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isKnownDurableFeedKind(kind: string): kind is FeedItemKind {
  return (
    kind === "message" ||
    kind === "feedback" ||
    kind === "objects" ||
    kind === "table" ||
    kind === "artifact" ||
    kind === "file" ||
    kind === "action_suggestion" ||
    kind === "prompt"
  );
}

function hasValidKnownFeedPayload(kind: string, value: unknown): boolean {
  const payload = asRecord(value);
  if (!payload) return false;
  switch (kind) {
    case "message":
      return (
        (payload.role === "user" ||
          payload.role === "assistant" ||
          payload.role === "system") &&
        typeof payload.text === "string"
      );
    case "feedback":
      return (
        typeof payload.text === "string" &&
        payload.audience === "customer" &&
        (payload.tone === "info" ||
          payload.tone === "working" ||
          payload.tone === "awaiting" ||
          payload.tone === "success" ||
          payload.tone === "warning" ||
          payload.tone === "error")
      );
    case "objects":
      return (
        Array.isArray(payload.refs) &&
        payload.refs.every((value) => {
          const ref = asRecord(value);
          return (
            (ref?.type === "entry" && isNonEmptyString(ref.path)) ||
            (ref?.type === "list" && isNonEmptyString(ref.name)) ||
            (ref?.type === "variable" && isNonEmptyString(ref.name))
          );
        })
      );
    case "table":
      return (
        isNonEmptyString(payload.tableId) &&
        Array.isArray(payload.columns) &&
        Array.isArray(payload.rows)
      );
    case "artifact": {
      const fallback = asRecord(payload.fallback);
      return (
        isNonEmptyString(payload.artifactId) &&
        Boolean(fallback) &&
        isNonEmptyString(fallback?.label) &&
        (fallback?.kind === "effect" ||
          fallback?.kind === "batch" ||
          fallback?.kind === "state_path")
      );
    }
    case "file": {
      const fallback = asRecord(payload.fallback);
      return (
        isNonEmptyString(payload.fileId) &&
        Boolean(fallback) &&
        isNonEmptyString(fallback?.filename)
      );
    }
    case "action_suggestion":
      return (
        isNonEmptyString(payload.suggestionId) &&
        isNonEmptyString(payload.label)
      );
    case "prompt":
      return (
        isNonEmptyString(payload.promptId) &&
        (payload.type === "choice" ||
          payload.type === "confirm" ||
          payload.type === "input") &&
        typeof payload.title === "string" &&
        typeof payload.message === "string" &&
        typeof payload.openedAt === "number" &&
        Number.isFinite(payload.openedAt)
      );
    default:
      // Unknown future kinds keep their sequence and opaque data. Older
      // clients may render an unsupported placeholder, but must not drop the
      // position and accidentally close a sequence gap.
      return true;
  }
}

function normalizeFeedItem(value: unknown): FeedItem | null {
  const item = asRecord(value);
  if (
    !item ||
    typeof item.id !== "string" ||
    !item.id ||
    !isSafeInteger(item.sequence, 1) ||
    typeof item.kind !== "string" ||
    !item.kind ||
    typeof item.occurredAt !== "number" ||
    !Number.isFinite(item.occurredAt) ||
    !isNonEmptyString(item.operationId) ||
    !isSafeInteger(item.batchIndex) ||
    !hasValidKnownFeedPayload(item.kind, item.payload)
  ) {
    return null;
  }

  // Unknown future kinds are intentionally retained. The current TypeScript
  // union cannot describe their payload, but preserving their identity and
  // sequence prevents an older client from reordering adjacent known items.
  return cloneAndFreeze(item) as unknown as FeedItem;
}

function normalizeTransientFeedItem(value: unknown): TransientFeedItem | null {
  const item = asRecord(value);
  const payload = asRecord(item?.payload);
  if (
    !item ||
    typeof item.id !== "string" ||
    !item.id ||
    (item.kind !== "message" && item.kind !== "feedback") ||
    !isSafeInteger(item.ordinal, 1) ||
    !isSafeInteger(item.revision, 1) ||
    typeof item.createdAt !== "number" ||
    !Number.isFinite(item.createdAt) ||
    typeof item.updatedAt !== "number" ||
    !Number.isFinite(item.updatedAt) ||
    !payload ||
    typeof payload.text !== "string" ||
    (item.kind === "message" &&
      (payload.role !== "assistant" ||
        !isSafeInteger(item.producerRevision, 0))) ||
    (item.kind === "feedback" &&
      (Object.prototype.hasOwnProperty.call(item, "producerRevision") ||
        payload.audience !== "customer" ||
        !isFeedTransientFeedbackTone(payload.tone)))
  ) {
    return null;
  }
  return cloneAndFreeze(item) as unknown as TransientFeedItem;
}

const FEED_TRANSIENT_FEEDBACK_TONES: ReadonlySet<FeedTransientFeedbackTone> =
  new Set(["info", "working", "awaiting", "warning", "error"]);

function isFeedTransientFeedbackTone(
  value: unknown,
): value is FeedTransientFeedbackTone {
  return (
    typeof value === "string" &&
    FEED_TRANSIENT_FEEDBACK_TONES.has(value as FeedTransientFeedbackTone)
  );
}

function normalizeFeedItems(values: unknown): {
  items: FeedItem[];
  error: Error | null;
} {
  if (!Array.isArray(values)) {
    return {
      items: [],
      error: new Error("Canonical feed tail is not an array."),
    };
  }

  const items: FeedItem[] = [];
  const ids = new Map<string, number>();
  const sequences = new Map<number, string>();
  const itemBySequence = new Map<number, FeedItem>();
  let error: Error | null = null;
  for (const value of values) {
    const item = normalizeFeedItem(value);
    if (!item) {
      error ||= new Error("Canonical feed contains an invalid durable item.");
      continue;
    }
    const idSequence = ids.get(item.id);
    const sequenceId = sequences.get(item.sequence);
    if (
      (idSequence !== undefined && idSequence !== item.sequence) ||
      (sequenceId !== undefined && sequenceId !== item.id)
    ) {
      error ||= new Error(
        `Canonical feed identity conflict at sequence ${item.sequence}.`,
      );
      continue;
    }
    if (idSequence === item.sequence || sequenceId === item.id) {
      const duplicate = itemBySequence.get(item.sequence);
      if (duplicate && !sameDurableItem(duplicate, item)) {
        error ||= new Error(
          `Immutable feed item ${item.id} has conflicting payloads.`,
        );
      }
      continue;
    }
    ids.set(item.id, item.sequence);
    sequences.set(item.sequence, item.id);
    itemBySequence.set(item.sequence, item);
    items.push(item);
  }
  items.sort((left, right) => left.sequence - right.sequence);
  return { items: Object.freeze(items) as FeedItem[], error };
}

function normalizeTransients(values: unknown): {
  items: TransientFeedItem[];
  error: Error | null;
} {
  const record = asRecord(values);
  if (!record) {
    return {
      items: [],
      error: new Error("Canonical feed transientById is not an object."),
    };
  }
  let error: Error | null = null;
  const byId = new Map<string, TransientFeedItem>();
  const idByOrdinal = new Map<number, string>();
  for (const [key, value] of Object.entries(record)) {
    const item = normalizeTransientFeedItem(value);
    if (!item || item.id !== key) {
      error ||= new Error("Canonical feed contains an invalid transient item.");
      continue;
    }
    const ordinalOwner = idByOrdinal.get(item.ordinal);
    if (ordinalOwner && ordinalOwner !== item.id) {
      error ||= new Error(
        `Canonical feed transient ordinal ${item.ordinal} is not unique.`,
      );
    } else {
      idByOrdinal.set(item.ordinal, item.id);
    }
    const current = byId.get(item.id);
    if (!current || item.revision > current.revision) {
      byId.set(item.id, item);
    }
  }
  const items = [...byId.values()].sort(
    (left, right) =>
      left.ordinal - right.ordinal || left.id.localeCompare(right.id),
  );
  return {
    items: Object.freeze(items) as TransientFeedItem[],
    error,
  };
}

export function readSessionFeedSnapshot(
  document: unknown,
  options: {
    isHydrated?: boolean;
    isRepairing?: boolean;
    error?: Error | null;
  } = {},
): FeedSnapshot {
  const isHydrated = options.isHydrated ?? true;
  if (!hasCanonicalSessionFeedActivation(document)) {
    return snapshotWith(emptyFeedSnapshot(isHydrated), {
      isRepairing: options.isRepairing || false,
      error:
        options.error ||
        (isHydrated
          ? new Error("Canonical session feed-v1 is required.")
          : null),
    });
  }

  const documentRecord = asRecord(document)!;
  const feed = asRecord(documentRecord.feed)!;
  const structureError = canonicalSessionFeedStructureError(document);
  if (structureError) {
    const lastSequence = finiteInteger(feed.lastSequence, 0);
    const archivedThroughSequence = finiteInteger(
      feed.archivedThroughSequence,
      0,
    );
    return snapshotWith(emptyFeedSnapshot(isHydrated), {
      revision: finiteInteger(feed.revision, 0),
      documentEpoch: finiteInteger(documentRecord.documentEpoch, 0),
      documentRevision: finiteInteger(documentRecord.documentRevision, 0),
      lastSequence,
      archivedThroughSequence,
      hasOlder: archivedThroughSequence > 0,
      isRepairing: options.isRepairing || false,
      error: options.error || structureError,
    });
  }

  const normalizedTail = normalizeFeedItems(feed.tail);
  const normalizedTransients = normalizeTransients(feed.transientById);
  const lastSequence = finiteInteger(feed.lastSequence, 0);
  const archivedThroughSequence = finiteInteger(
    feed.archivedThroughSequence,
    0,
  );
  let error =
    options.error || normalizedTail.error || normalizedTransients.error;

  let expected = archivedThroughSequence + 1;
  for (const item of normalizedTail.items) {
    if (item.sequence !== expected) {
      error ||= new Error(
        `Canonical feed tail has a sequence gap before ${item.sequence}; expected ${expected}.`,
      );
      break;
    }
    expected += 1;
  }
  const tailLastSequence =
    normalizedTail.items[normalizedTail.items.length - 1]?.sequence ||
    archivedThroughSequence;
  if (tailLastSequence !== lastSequence) {
    error ||= new Error(
      `Canonical feed tail ends at ${tailLastSequence}, but lastSequence is ${lastSequence}.`,
    );
  }
  if (archivedThroughSequence > lastSequence) {
    error ||= new Error(
      "Canonical feed archivedThroughSequence exceeds lastSequence.",
    );
  }

  return Object.freeze({
    tail: normalizedTail.items,
    transients: normalizedTransients.items,
    revision: finiteInteger(feed.revision, 0),
    documentEpoch: finiteInteger(documentRecord.documentEpoch, 0),
    documentRevision: finiteInteger(documentRecord.documentRevision, 0),
    lastSequence,
    archivedThroughSequence,
    hasOlder: archivedThroughSequence > 0,
    isHydrated,
    isRepairing: options.isRepairing || false,
    error,
  });
}

function validateFeedListOptions(options: FeedListOptions): FeedListOptions {
  if (
    options.afterSequence !== undefined &&
    options.beforeSequence !== undefined
  ) {
    throw new RangeError(
      "Feed list accepts afterSequence or beforeSequence, not both.",
    );
  }
  for (const [name, value] of [
    ["afterSequence", options.afterSequence],
    ["beforeSequence", options.beforeSequence],
  ] as const) {
    if (value !== undefined && !isSafeInteger(value)) {
      throw new RangeError(`${name} must be a non-negative integer.`);
    }
  }
  if (
    options.limit !== undefined &&
    (!isSafeInteger(options.limit, 1) || options.limit > 500)
  ) {
    throw new RangeError("Feed list limit must be an integer from 1 to 500.");
  }
  return { ...options };
}

export function normalizeFeedPage(value: unknown): FeedPage {
  const page = asRecord(value);
  if (!page) {
    throw new Error("Feed list transport returned an invalid page.");
  }
  const normalized = normalizeFeedItems(page.items);
  if (normalized.error) {
    throw normalized.error;
  }
  const first = normalized.items[0]?.sequence || null;
  const last = normalized.items[normalized.items.length - 1]?.sequence || null;
  for (let index = 1; index < normalized.items.length; index += 1) {
    if (
      normalized.items[index]!.sequence !==
      normalized.items[index - 1]!.sequence + 1
    ) {
      throw new Error("Feed list page contains a sequence gap.");
    }
  }
  if (
    (first === null &&
      (page.firstSequence !== null || page.lastSequence !== null)) ||
    (first !== null &&
      (!isSafeInteger(page.firstSequence, 1) ||
        !isSafeInteger(page.lastSequence, 1) ||
        page.firstSequence !== first ||
        page.lastSequence !== last)) ||
    typeof page.hasMoreBefore !== "boolean" ||
    typeof page.hasMoreAfter !== "boolean"
  ) {
    throw new Error(
      "Feed list page sequence metadata does not match its items.",
    );
  }

  return Object.freeze({
    items: normalized.items,
    firstSequence: first,
    lastSequence: last,
    hasMoreBefore: page.hasMoreBefore,
    hasMoreAfter: page.hasMoreAfter,
  });
}

type FeedSubscriber = (change: FeedSubscriptionChange) => void;

function stableValueFingerprint(
  value: unknown,
  ancestors = new WeakSet<object>(),
): string {
  if (!value || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (ancestors.has(value as object)) return '"[circular]"';
  ancestors.add(value as object);
  const result = Array.isArray(value)
    ? `[${value
        .map((entry) => stableValueFingerprint(entry, ancestors))
        .join(",")}]`
    : `{${Object.keys(value as UnknownRecord)
        .sort()
        .map(
          (key) =>
            `${JSON.stringify(key)}:${stableValueFingerprint(
              (value as UnknownRecord)[key],
              ancestors,
            )}`,
        )
        .join(",")}}`;
  ancestors.delete(value as object);
  return result;
}

function sameDurableItem(left: FeedItem, right: FeedItem): boolean {
  return stableValueFingerprint(left) === stableValueFingerprint(right);
}

/**
 * Highest sequence that is actually present in this client-side snapshot.
 *
 * `archivedThroughSequence` describes what the server has compacted out of
 * the live Automerge document. It is not evidence that a newly connected
 * browser has loaded those archived entries. Treating it as a local delivery
 * watermark makes an all-archived feed look complete and loses the entire
 * visible history until a later full reload.
 */
function contiguousLocalTailWatermark(snapshot: FeedSnapshot): number {
  let watermark = 0;
  for (const item of snapshot.tail) {
    if (item.sequence !== watermark + 1) break;
    watermark = item.sequence;
  }
  return watermark;
}

/**
 * Stateful bridge between replicated Automerge snapshots and the public feed.
 *
 * It owns sequence deduplication, stale snapshot rejection and history repair.
 * UI consumers never merge raw websocket events with this stream.
 */
export class SessionFeedController implements SessionFeedApi {
  private snapshot: FeedSnapshot;
  private canonical: boolean;
  private canonicalStructureValid: boolean;
  private deliveredThrough: number;
  private knownBySequence = new Map<number, FeedItem>();
  private sequenceById = new Map<string, number>();
  private subscribers = new Set<FeedSubscriber>();
  private listTransport: FeedListTransport | null;
  private repairGeneration = 0;
  private repairPromise: Promise<void> | null = null;
  private repairRetryHandle: unknown | null = null;
  private repairRetryToken = 0;
  private repairFailureAttempt = 0;
  private scheduleRepairRetryCallback: (
    callback: () => void,
    delayMs: number,
  ) => unknown;
  private cancelRepairRetryCallback: (handle: unknown) => void;
  private initialRepairRetryDelayMs: number;
  private maxRepairRetryDelayMs: number;
  private disposed = false;
  private diagnosticListener: FeedDiagnosticListener | null;
  private diagnosticNow: () => number;
  private observedUnknownPositions = new Set<string>();

  constructor(
    initialDocument: unknown,
    options: SessionFeedControllerOptions = {},
  ) {
    this.diagnosticListener = options.onDiagnostic || null;
    this.diagnosticNow = options.now || Date.now;
    this.scheduleRepairRetryCallback =
      options.scheduleRepairRetry ||
      ((callback, delayMs) => setTimeout(callback, delayMs));
    this.cancelRepairRetryCallback =
      options.cancelRepairRetry ||
      ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.initialRepairRetryDelayMs = Math.max(
      1,
      options.initialRepairRetryDelayMs ?? 500,
    );
    this.maxRepairRetryDelayMs = Math.max(
      this.initialRepairRetryDelayMs,
      options.maxRepairRetryDelayMs ?? 10_000,
    );
    this.canonical = hasCanonicalSessionFeedActivation(initialDocument);
    this.canonicalStructureValid =
      this.canonical &&
      canonicalSessionFeedStructureError(initialDocument) === null;
    this.snapshot = readSessionFeedSnapshot(initialDocument, {
      isHydrated: options.isHydrated ?? this.canonical,
    });
    this.listTransport = options.listTransport || null;
    this.deliveredThrough = contiguousLocalTailWatermark(this.snapshot);
    const identityError = this.remember(this.snapshot.tail);
    const needsRepair =
      this.canonicalStructureValid &&
      this.deliveredThrough < this.snapshot.lastSequence;
    if (identityError || needsRepair) {
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: needsRepair && Boolean(this.listTransport),
        error: identityError || this.snapshot.error,
      });
    }
    if (needsRepair) {
      this.startRepair(this.snapshot.lastSequence);
    }
  }

  setListTransport(transport: FeedListTransport | null): void {
    if (this.disposed) return;
    if (transport !== this.listTransport) {
      this.cancelScheduledRepairRetry();
      this.repairFailureAttempt = 0;
      this.repairGeneration += 1;
      this.repairPromise = null;
    }
    this.listTransport = transport;
    if (
      transport &&
      this.canonical &&
      this.canonicalStructureValid &&
      this.deliveredThrough < this.snapshot.lastSequence
    ) {
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: true,
        error: null,
      });
      this.startRepair(this.snapshot.lastSequence);
    }
  }

  getSnapshot(): FeedSnapshot {
    return this.snapshot;
  }

  /**
   * Stop background archive work when its owning Session is replaced or
   * explicitly disconnected. Late transport completions are quarantined by
   * the generation check and cannot update subscribers.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelScheduledRepairRetry();
    this.repairGeneration += 1;
    this.repairPromise = null;
    this.listTransport = null;
    this.subscribers.clear();
  }

  async list(options: FeedListOptions = {}): Promise<FeedPage> {
    const validated = validateFeedListOptions(options);
    if (!this.listTransport) {
      throw new Error(
        "Historical feed transport is unavailable for this Session.",
      );
    }
    const page = normalizeFeedPage(await this.listTransport(validated));
    this.observeUnknownKinds(page.items);
    return page;
  }

  subscribe(
    listener: FeedSubscriber,
    options: FeedSubscribeOptions = {},
  ): () => void {
    const afterSequence = options.afterSequence;
    if (afterSequence !== undefined && !isSafeInteger(afterSequence)) {
      throw new RangeError(
        "Feed afterSequence must be a non-negative safe integer.",
      );
    }
    this.subscribers.add(listener);
    if (
      afterSequence !== undefined &&
      afterSequence < this.snapshot.lastSequence
    ) {
      const items = this.snapshot.tail.filter(
        (item) => item.sequence > afterSequence,
      );
      if (
        items.length > 0 &&
        items[0]!.sequence === afterSequence + 1 &&
        items[items.length - 1]!.sequence === this.snapshot.lastSequence
      ) {
        listener({ type: "append", items });
        if (this.snapshot.transients.length > 0) {
          listener({
            type: "transients",
            items: this.snapshot.transients,
            revision: this.snapshot.revision,
          });
        }
      } else {
        listener({ type: "reset", snapshot: this.snapshot });
      }
    } else {
      listener({ type: "reset", snapshot: this.snapshot });
    }
    return () => {
      this.subscribers.delete(listener);
    };
  }

  /**
   * Accept the latest synced document. Calls may arrive out of order after a
   * reconnect; freshness watermarks prevent an older snapshot from regressing
   * durable positions or transient state.
   */
  updateDocument(
    document: unknown,
    options: { forceReset?: boolean } = {},
  ): void {
    if (this.disposed) return;
    const nextCanonical = hasCanonicalSessionFeedActivation(document);
    const next = readSessionFeedSnapshot(document, { isHydrated: true });

    if (!nextCanonical) {
      if (!this.canonical) {
        this.snapshot = next;
        this.emit({ type: "reset", snapshot: this.snapshot });
        return;
      }
      // Canonical activation is sticky. A later non-canonical document can
      // only be stale/corrupt, so never erase accepted history.
      this.rejectSnapshotRegression("canonical_deactivation", next);
      return;
    }

    const nextStructureError = canonicalSessionFeedStructureError(document);
    if (nextStructureError) {
      if (!this.canonical) {
        this.canonical = true;
        this.canonicalStructureValid = false;
        this.cancelScheduledRepairRetry();
        this.repairFailureAttempt = 0;
        this.repairGeneration += 1;
        this.repairPromise = null;
        this.deliveredThrough = 0;
        this.knownBySequence.clear();
        this.sequenceById.clear();
        this.observedUnknownPositions.clear();
        this.snapshot = next;
        this.emit({ type: "reset", snapshot: this.snapshot });
        return;
      }

      this.rejectSnapshotRegression("invalid_canonical_document", next);
      if (!this.canonicalStructureValid) {
        const current = this.snapshot;
        const incomingIsNewer =
          next.documentEpoch > current.documentEpoch ||
          (next.documentEpoch === current.documentEpoch &&
            next.documentRevision >= current.documentRevision);
        if (incomingIsNewer && next.lastSequence >= current.lastSequence) {
          this.snapshot = next;
        }
      } else {
        this.snapshot = snapshotWith(this.snapshot, {
          error: nextStructureError,
        });
      }
      this.emit({ type: "reset", snapshot: this.snapshot });
      return;
    }

    if (!this.canonical || options.forceReset) {
      this.acceptReset(next);
      return;
    }
    if (!this.canonicalStructureValid) {
      const current = this.snapshot;
      if (next.documentEpoch < current.documentEpoch) {
        this.rejectSnapshotRegression("older_document_epoch", next);
        return;
      }
      if (next.lastSequence < current.lastSequence) {
        this.rejectSnapshotRegression("last_sequence_regression", next);
        return;
      }
      if (
        next.documentEpoch === current.documentEpoch &&
        next.documentRevision < current.documentRevision
      ) {
        this.rejectSnapshotRegression("document_revision_regression", next);
        return;
      }
      if (
        next.documentEpoch === current.documentEpoch &&
        next.revision < current.revision
      ) {
        this.rejectSnapshotRegression("feed_revision_regression", next);
        return;
      }
      this.acceptReset(next);
      return;
    }

    const current = this.snapshot;
    if (next.documentEpoch < current.documentEpoch) {
      this.rejectSnapshotRegression("older_document_epoch", next);
      return;
    }
    if (next.documentEpoch > current.documentEpoch) {
      if (next.lastSequence < current.lastSequence) {
        this.rejectSnapshotRegression("last_sequence_regression", next);
        return;
      }
      const identityError = this.findIdentityConflict(next.tail);
      if (identityError) {
        this.rejectSnapshotRegression("identity_conflict", next);
        this.snapshot = snapshotWith(current, { error: identityError });
        this.emit({ type: "reset", snapshot: this.snapshot });
        return;
      }
      this.acceptReset(next);
      return;
    }

    if (next.lastSequence < current.lastSequence) {
      this.rejectSnapshotRegression("last_sequence_regression", next);
      if (next.documentRevision > current.documentRevision) {
        this.emit({
          type: "resource_refresh",
          documentRevision: next.documentRevision,
        });
        this.snapshot = snapshotWith(current, {
          documentRevision: next.documentRevision,
        });
      }
      return;
    }

    const previousDocumentRevision = current.documentRevision;
    const previousRevision = current.revision;
    if (
      next.lastSequence > current.lastSequence &&
      next.revision <= previousRevision
    ) {
      this.rejectSnapshotRegression("last_sequence_without_revision", next);
      const documentRevision = Math.max(
        previousDocumentRevision,
        next.documentRevision,
      );
      this.snapshot = snapshotWith(current, {
        documentRevision,
        error: new Error(
          "Feed lastSequence advanced without a newer feed revision.",
        ),
      });
      if (next.documentRevision > previousDocumentRevision) {
        this.emit({
          type: "resource_refresh",
          documentRevision: next.documentRevision,
        });
      }
      this.emit({ type: "reset", snapshot: this.snapshot });
      return;
    }

    const previousTransients = current.transients;
    const transientIdentityError =
      next.revision === previousRevision &&
      !sameTransientSet(previousTransients, next.transients)
        ? new Error("Transient feed changed without a newer feed revision.")
        : null;
    const transientSelection = this.selectNewerTransients(current, next);
    const mergedTransients = transientSelection.items;
    const identityError =
      transientIdentityError ||
      transientSelection.error ||
      this.remember(next.tail);
    if (identityError) {
      this.rejectSnapshotRegression("identity_conflict", next);
      this.snapshot = snapshotWith(current, {
        documentRevision: Math.max(
          previousDocumentRevision,
          next.documentRevision,
        ),
        error: identityError,
      });
      this.emit({ type: "reset", snapshot: this.snapshot });
      return;
    }

    const keepCurrentFeedState =
      next.lastSequence === current.lastSequence &&
      next.revision < current.revision;
    if (keepCurrentFeedState) {
      this.rejectSnapshotRegression("feed_revision_regression", next);
    }

    let nextSnapshot = snapshotWith(keepCurrentFeedState ? current : next, {
      transients: mergedTransients,
      revision: Math.max(previousRevision, next.revision),
      documentRevision: Math.max(
        previousDocumentRevision,
        next.documentRevision,
      ),
    });

    const appended =
      next.lastSequence > this.deliveredThrough
        ? this.drainContiguous(next.lastSequence)
        : [];

    const needsRepair = this.deliveredThrough < next.lastSequence;
    const incomingAdvanced =
      next.documentEpoch > current.documentEpoch ||
      next.documentRevision > current.documentRevision ||
      next.revision > current.revision ||
      next.lastSequence > current.lastSequence;
    if (incomingAdvanced || !needsRepair) {
      this.cancelScheduledRepairRetry();
      this.repairFailureAttempt = 0;
    }
    nextSnapshot = snapshotWith(nextSnapshot, {
      isRepairing: needsRepair,
      error: nextSnapshot.error,
    });
    this.snapshot = nextSnapshot;

    if (appended.length > 0) {
      this.emit({ type: "append", items: appended });
    }

    if (
      !sameTransientSet(previousTransients, mergedTransients) &&
      next.revision > previousRevision
    ) {
      this.emit({
        type: "transients",
        items: mergedTransients,
        revision: this.snapshot.revision,
      });
    }

    if (next.documentRevision > previousDocumentRevision) {
      this.emit({
        type: "resource_refresh",
        documentRevision: next.documentRevision,
      });
    }

    if (needsRepair) {
      this.startRepair(next.lastSequence);
    }
  }

  /**
   * Quarantine a canonical replacement rejected by the document transport.
   * The notice deliberately contains no replacement document, so accepted
   * feed history remains the only data visible to subscribers during repair.
   *
   * @internal
   */
  quarantineCanonicalReplacement(quarantine: {
    documentEpoch: number;
    documentRevision: number;
    error: Error;
  }): void {
    if (this.disposed) return;
    const incoming = snapshotWith(emptyFeedSnapshot(true), {
      documentEpoch: quarantine.documentEpoch,
      documentRevision: quarantine.documentRevision,
      isRepairing: true,
      error: quarantine.error,
    });

    if (!this.canonical) {
      this.canonical = true;
      this.canonicalStructureValid = false;
      this.repairGeneration += 1;
      this.repairPromise = null;
      this.cancelScheduledRepairRetry();
      this.repairFailureAttempt = 0;
      this.deliveredThrough = 0;
      this.knownBySequence.clear();
      this.sequenceById.clear();
      this.observedUnknownPositions.clear();
      this.snapshot = incoming;
      this.emit({ type: "reset", snapshot: this.snapshot });
      return;
    }

    this.rejectSnapshotRegression("invalid_canonical_document", incoming);
    this.repairGeneration += 1;
    this.repairPromise = null;
    this.cancelScheduledRepairRetry();
    this.repairFailureAttempt = 0;
    if (!this.canonicalStructureValid) {
      const current = this.snapshot;
      const incomingIsNewer =
        incoming.documentEpoch > current.documentEpoch ||
        (incoming.documentEpoch === current.documentEpoch &&
          incoming.documentRevision >= current.documentRevision);
      if (incomingIsNewer) {
        this.snapshot = incoming;
      } else {
        this.snapshot = snapshotWith(current, {
          isRepairing: true,
          error: quarantine.error,
        });
      }
    } else {
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: true,
        error: quarantine.error,
      });
    }
    this.emit({ type: "reset", snapshot: this.snapshot });
  }

  private acceptReset(snapshot: FeedSnapshot): void {
    this.cancelScheduledRepairRetry();
    this.repairFailureAttempt = 0;
    this.repairGeneration += 1;
    this.repairPromise = null;
    this.canonical = true;
    this.canonicalStructureValid = true;
    this.knownBySequence.clear();
    this.sequenceById.clear();
    this.observedUnknownPositions.clear();
    this.deliveredThrough = contiguousLocalTailWatermark(snapshot);
    const identityError = this.remember(snapshot.tail);
    const needsRepair = this.deliveredThrough < snapshot.lastSequence;
    this.snapshot = snapshotWith(snapshot, {
      isRepairing: needsRepair && Boolean(this.listTransport),
      error: identityError || snapshot.error,
    });
    this.emit({ type: "reset", snapshot: this.snapshot });
    if (needsRepair) {
      this.startRepair(snapshot.lastSequence);
    }
  }

  private emitDiagnostic(diagnostic: FeedDiagnostic): void {
    emitFeedDiagnostic(diagnostic, this.diagnosticListener);
  }

  private rejectSnapshotRegression(
    reason: FeedSnapshotRegressionReason,
    incoming: FeedSnapshot,
  ): void {
    const current = this.snapshot;
    this.emitDiagnostic({
      type: "snapshot_regression_rejected",
      reason,
      currentDocumentEpoch: current.documentEpoch,
      incomingDocumentEpoch: incoming.documentEpoch,
      currentDocumentRevision: current.documentRevision,
      incomingDocumentRevision: incoming.documentRevision,
      currentFeedRevision: current.revision,
      incomingFeedRevision: incoming.revision,
      currentLastSequence: current.lastSequence,
      incomingLastSequence: incoming.lastSequence,
    });
  }

  private observeUnknownKinds(items: FeedItem[]): void {
    for (const item of items) {
      const runtimeKind = String(item.kind);
      if (isKnownDurableFeedKind(runtimeKind)) continue;
      const diagnosticKind = normalizeFeedDiagnosticKind(runtimeKind);
      const position = `${item.sequence}:${diagnosticKind}`;
      if (this.observedUnknownPositions.has(position)) continue;
      this.observedUnknownPositions.add(position);
      this.emitDiagnostic({
        type: "unknown_kind",
        kind: diagnosticKind,
        sequence: item.sequence,
        consumer: "sdk",
      });
    }
  }

  private selectNewerTransients(
    current: FeedSnapshot,
    next: FeedSnapshot,
  ): { items: TransientFeedItem[]; error: Error | null } {
    if (next.revision <= current.revision) {
      return { items: current.transients, error: null };
    }

    let newestById: Map<string, TransientFeedItem>;
    try {
      newestById = new Map(
        orderTransientFeedItems([
          ...current.transients,
          ...next.transients,
        ]).map((item) => [item.id, item]),
      );
    } catch {
      return {
        items: current.transients,
        error: new Error(
          "Transient feed changed immutable identity or revision.",
        ),
      };
    }

    const currentById = new Map(
      current.transients.map((item) => [item.id, item]),
    );
    const selected: TransientFeedItem[] = [];
    for (const incoming of next.transients) {
      const previous = currentById.get(incoming.id);
      if (previous && previous.kind !== incoming.kind) {
        return {
          items: current.transients,
          error: new Error(
            `Transient feed item ${incoming.id} changed its immutable kind.`,
          ),
        };
      }

      if (
        previous?.kind === "message" &&
        incoming.kind === "message" &&
        incoming.revision > previous.revision &&
        incoming.producerRevision < previous.producerRevision
      ) {
        selected.push(previous);
        continue;
      }
      selected.push(newestById.get(incoming.id) || incoming);
    }

    return {
      items: orderTransientFeedItems(selected),
      error: null,
    };
  }

  private remember(items: FeedItem[]): Error | null {
    const stagedBySequence = new Map<number, FeedItem>();
    const stagedSequenceById = new Map<string, number>();
    for (const item of items) {
      const knownSequence =
        stagedSequenceById.get(item.id) ?? this.sequenceById.get(item.id);
      const knownItem =
        stagedBySequence.get(item.sequence) ||
        this.knownBySequence.get(item.sequence);
      if (
        (knownSequence !== undefined && knownSequence !== item.sequence) ||
        (knownItem && knownItem.id !== item.id)
      ) {
        return new Error(
          `Feed identity changed at sequence ${item.sequence}; a reset is required.`,
        );
      }
      if (knownItem && !sameDurableItem(knownItem, item)) {
        return new Error(
          `Immutable feed item ${item.id} changed at sequence ${item.sequence}.`,
        );
      }
      stagedSequenceById.set(item.id, item.sequence);
      stagedBySequence.set(item.sequence, item);
    }
    for (const item of stagedBySequence.values()) {
      this.sequenceById.set(item.id, item.sequence);
      this.knownBySequence.set(item.sequence, item);
    }
    this.observeUnknownKinds([...stagedBySequence.values()]);
    return null;
  }

  private findIdentityConflict(items: FeedItem[]): Error | null {
    for (const item of items) {
      const knownSequence = this.sequenceById.get(item.id);
      const knownItem = this.knownBySequence.get(item.sequence);
      if (
        (knownSequence !== undefined && knownSequence !== item.sequence) ||
        (knownItem && knownItem.id !== item.id)
      ) {
        return new Error(
          `Feed identity changed at sequence ${item.sequence}; the checkpoint was rejected.`,
        );
      }
      if (knownItem && !sameDurableItem(knownItem, item)) {
        return new Error(
          `Immutable feed item ${item.id} changed at sequence ${item.sequence}.`,
        );
      }
    }
    return null;
  }

  private drainContiguous(targetSequence: number): FeedItem[] {
    const appended: FeedItem[] = [];
    let sequence = this.deliveredThrough + 1;
    while (sequence <= targetSequence) {
      const item = this.knownBySequence.get(sequence);
      if (!item) break;
      appended.push(item);
      this.deliveredThrough = sequence;
      sequence += 1;
    }
    return Object.freeze(appended) as FeedItem[];
  }

  private startRepair(targetSequence: number): void {
    if (
      this.disposed ||
      this.repairPromise ||
      this.repairRetryHandle !== null
    ) {
      return;
    }
    if (!this.listTransport) {
      this.emitDiagnostic({
        type: "gap_detected",
        expectedSequence: this.deliveredThrough + 1,
        targetSequence,
        repairAvailable: false,
      });
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: false,
        error:
          this.snapshot.error ||
          new Error(
            "Feed sequence gap cannot be repaired without history transport.",
          ),
      });
      this.emit({ type: "reset", snapshot: this.snapshot });
      return;
    }

    this.emitDiagnostic({
      type: "gap_detected",
      expectedSequence: this.deliveredThrough + 1,
      targetSequence,
      repairAvailable: true,
    });
    const generation = ++this.repairGeneration;
    this.repairPromise = this.repair(targetSequence, generation).finally(() => {
      if (generation === this.repairGeneration) {
        this.repairPromise = null;
        if (this.deliveredThrough < this.snapshot.lastSequence) {
          if (this.snapshot.error) {
            this.scheduleQuietRepairRetry();
          } else {
            this.snapshot = snapshotWith(this.snapshot, { isRepairing: true });
            this.startRepair(this.snapshot.lastSequence);
          }
        } else {
          this.repairFailureAttempt = 0;
        }
      }
    });
  }

  private cancelScheduledRepairRetry(): void {
    if (this.repairRetryHandle === null) return;
    const handle = this.repairRetryHandle;
    this.repairRetryHandle = null;
    this.repairRetryToken += 1;
    this.cancelRepairRetryCallback(handle);
  }

  private scheduleQuietRepairRetry(): void {
    if (
      this.disposed ||
      this.repairRetryHandle !== null ||
      this.repairPromise ||
      !this.listTransport ||
      this.deliveredThrough >= this.snapshot.lastSequence
    ) {
      return;
    }
    this.repairFailureAttempt += 1;
    const delayMs = Math.min(
      this.maxRepairRetryDelayMs,
      this.initialRepairRetryDelayMs *
        2 ** Math.min(30, Math.max(0, this.repairFailureAttempt - 1)),
    );
    const retryToken = ++this.repairRetryToken;
    let handle: unknown;
    handle = this.scheduleRepairRetryCallback(() => {
      if (
        retryToken !== this.repairRetryToken ||
        this.repairRetryHandle !== handle
      ) {
        return;
      }
      this.repairRetryHandle = null;
      if (this.disposed) return;
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: true,
        error: null,
      });
      this.emit({ type: "reset", snapshot: this.snapshot });
      this.startRepair(this.snapshot.lastSequence);
    }, delayMs);
    this.repairRetryHandle = handle;
  }

  private async repair(
    targetSequence: number,
    generation: number,
  ): Promise<void> {
    const startedAt = this.diagnosticNow();
    let pageCount = 0;
    let outcome: "success" | "failure" | "cancelled" = "success";
    try {
      let attempts = 0;
      while (
        generation === this.repairGeneration &&
        this.deliveredThrough < targetSequence
      ) {
        if (++attempts > 100) {
          throw new Error("Feed gap repair exceeded its page limit.");
        }
        const page = await this.list({
          afterSequence: this.deliveredThrough,
          limit: 500,
        });
        pageCount += 1;
        if (generation !== this.repairGeneration) {
          outcome = "cancelled";
          return;
        }
        if (page.items.length === 0) {
          throw new Error(
            `Feed gap repair returned no item after sequence ${this.deliveredThrough}.`,
          );
        }
        const deliveredBeforePage = this.deliveredThrough;
        const identityError = this.remember(page.items);
        if (identityError) throw identityError;
        const appended = this.drainContiguous(targetSequence);
        const reachedTarget = this.deliveredThrough >= targetSequence;
        if (reachedTarget) {
          this.snapshot = snapshotWith(this.snapshot, {
            isRepairing: this.deliveredThrough < this.snapshot.lastSequence,
            error: null,
          });
        }
        if (appended.length > 0) {
          this.emit({ type: "append", items: appended });
        }
        if (this.deliveredThrough === deliveredBeforePage) {
          throw new Error(
            `Feed gap repair did not return expected sequence ${
              deliveredBeforePage + 1
            }.`,
          );
        }
        if (
          this.deliveredThrough < targetSequence &&
          !page.hasMoreAfter &&
          (page.lastSequence || 0) < targetSequence
        ) {
          throw new Error(
            `Feed gap remains after sequence ${this.deliveredThrough}.`,
          );
        }
      }
      if (generation !== this.repairGeneration) {
        outcome = "cancelled";
        return;
      }
    } catch (error) {
      if (generation !== this.repairGeneration) {
        outcome = "cancelled";
        return;
      }
      outcome = "failure";
      this.snapshot = snapshotWith(this.snapshot, {
        isRepairing: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
      this.emit({ type: "reset", snapshot: this.snapshot });
    } finally {
      this.emitDiagnostic({
        type: "gap_repair",
        outcome,
        durationMs: Math.max(0, this.diagnosticNow() - startedAt),
        pageCount,
        repairedThroughSequence: this.deliveredThrough,
        targetSequence,
      });
    }
  }

  private emit(change: FeedSubscriptionChange): void {
    for (const subscriber of this.subscribers) {
      try {
        subscriber(change);
      } catch (error) {
        console.error("[Granular] Session feed subscriber failed", error);
      }
    }
  }
}

function sameTransientSet(
  left: TransientFeedItem[],
  right: TransientFeedItem[],
): boolean {
  return stableValueFingerprint(left) === stableValueFingerprint(right);
}

function operationId(prefix: string): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  return randomUuid
    ? `${prefix}_${randomUuid}`
    : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export interface PublishFeedbackOptions {
  icon?: FeedIconToken;
  tone?: FeedFeedbackTone;
  code?: string;
  operationId?: string;
}

export interface PublishTransientFeedbackOptions {
  icon?: FeedIconToken;
  tone?: FeedTransientFeedbackTone;
  expiresAt?: number;
  operationId?: string;
}

export interface SettleTransientFeedbackOptions {
  icon?: FeedIconToken;
  tone?: FeedFeedbackTone;
  code?: string;
  operationId?: string;
  /** Set false to remove the live row without creating durable history. */
  durable?: boolean;
}

export interface TransientFeedbackHandle {
  readonly id: string;
  readonly ordinal: number;
  readonly revision: number;
  update(
    text: string,
    options?: Omit<PublishTransientFeedbackOptions, "operationId"> & {
      operationId?: string;
    },
  ): Promise<TransientFeedbackHandle>;
  settle(
    text?: string,
    options?: SettleTransientFeedbackOptions,
  ): Promise<FeedbackFeedItem | null>;
}

export interface FeedPublisher {
  feedback(
    text: string,
    options?: PublishFeedbackOptions,
  ): Promise<FeedbackFeedItem>;
  transientFeedback(
    text: string,
    options?: PublishTransientFeedbackOptions,
  ): Promise<TransientFeedbackHandle>;
}

export type FeedPublishTransport = (
  method: string,
  params: Record<string, unknown>,
) => Promise<unknown>;

function unwrapPublishedItem(value: unknown): unknown {
  const record = asRecord(value);
  const directItems = Array.isArray(record?.items) ? record.items : [];
  const durable = asRecord(record?.durable);
  const durableItems = Array.isArray(durable?.items) ? durable.items : [];
  return record?.item || directItems[0] || durableItems[0] || value;
}

function requireFeedbackItem(value: unknown): FeedbackFeedItem {
  const item = normalizeFeedItem(unwrapPublishedItem(value));
  if (!item || item.kind !== "feedback") {
    throw new Error("Feed publisher returned an invalid feedback item.");
  }
  return item;
}

function requireTransientFeedbackItem(
  value: unknown,
): TransientFeedbackFeedItem {
  const item = normalizeTransientFeedItem(unwrapPublishedItem(value));
  if (!item || item.kind !== "feedback") {
    throw new Error(
      "Feed publisher returned an invalid transient feedback item.",
    );
  }
  return item;
}

/**
 * Build customer-visible feedback helpers for a trusted, invocation-scoped
 * transport. This is infrastructure for effect/grounding execution hosts; it
 * is intentionally not attached to browser Session instances.
 */
export function createFeedPublisher(
  publish: FeedPublishTransport,
): FeedPublisher {
  const makeHandle = (
    initial: TransientFeedbackFeedItem,
  ): TransientFeedbackHandle => {
    let current = initial;
    let mutationQueue: Promise<void> = Promise.resolve();
    const enqueueMutation = <T>(mutation: () => Promise<T>): Promise<T> => {
      const result = mutationQueue.then(mutation);
      // A failed mutation rejects its own caller without permanently poisoning
      // later authored operations. Those operations still execute in call order
      // against the newest successfully acknowledged revision.
      mutationQueue = result.then(
        () => undefined,
        () => undefined,
      );
      return result;
    };
    const handle: TransientFeedbackHandle = {
      get id() {
        return current.id;
      },
      get ordinal() {
        return current.ordinal;
      },
      get revision() {
        return current.revision;
      },
      update(text, options = {}) {
        // Reserve identity and snapshot caller-owned options synchronously.
        // The CAS revision itself is deliberately read only when this mutation
        // reaches the head of the per-handle queue.
        const reservedOperationId =
          options.operationId || operationId("feed_transient_update");
        const reservedOptions = { ...options };
        return enqueueMutation(async () => {
          const response = await publish("feed.transient.update", {
            transientId: current.id,
            expectedRevision: current.revision,
            text,
            ...reservedOptions,
            operationId: reservedOperationId,
          });
          current = requireTransientFeedbackItem(response);
          return handle;
        });
      },
      settle(text, options = {}) {
        const reservedOperationId =
          options.operationId || operationId("feed_transient_settle");
        const reservedOptions = { ...options };
        return enqueueMutation(async () => {
          const response = await publish("feed.transient.settle", {
            transientId: current.id,
            expectedRevision: current.revision,
            ...(text === undefined ? {} : { text }),
            ...reservedOptions,
            operationId: reservedOperationId,
          });
          const responseRecord = asRecord(response);
          const durable = asRecord(responseRecord?.durable);
          const durableItems = Array.isArray(durable?.items)
            ? durable.items
            : [];
          const rawItem =
            responseRecord?.item ??
            responseRecord?.durableItem ??
            durableItems[0];
          if (rawItem === null || rawItem === undefined) {
            return null;
          }
          return requireFeedbackItem(rawItem);
        });
      },
    };
    return handle;
  };

  return {
    async feedback(text, options = {}) {
      return requireFeedbackItem(
        await publish("feed.feedback", {
          text,
          ...options,
          operationId: options.operationId || operationId("feed_feedback"),
        }),
      );
    },
    async transientFeedback(text, options = {}) {
      const item = requireTransientFeedbackItem(
        await publish("feed.transient.create", {
          text,
          ...options,
          operationId:
            options.operationId || operationId("feed_transient_create"),
        }),
      );
      return makeHandle(item);
    },
  };
}
